import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getSetupStatus } from "../api/airwallex";
import WalletDashboard from "./dashboard/WalletDashboard";
import { AIRWALLEX_CONFIG } from "../config/airwallex";
import LoadingSpinner from "../components/common/LoadingSpinner";

/**
 * Home component that redirects based on wallet account status
 * - If account status is ACTIVE: Show WalletDashboard
 * - Otherwise: Redirect to /wallet/setup for onboarding
 */
const Home = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accountStatus, setAccountStatus] = useState(null);
  const [balances, setBalances] = useState([]);

  const checkAccountStatus = useCallback(async (showLoadingScreen = true) => {
    if (showLoadingScreen) {
      setLoading(true);
    }

    try {
      // Fetch account details
      const response = await getSetupStatus();

      // Handle HTTP 204 No Content - no account exists yet
      if (response.status === 204) {
        navigate("/wallet/setup");
        return;
      }

      // Handle HTTP 200 with data
      if (response.status === 200 && response.data) {
        const { status, balances: accountBalances } = response.data;
        setAccountStatus(status);
        setBalances(accountBalances || []);

        // Calculate total balance
        const totalBalance = (accountBalances || []).reduce((sum, balance) => {
          return sum + (parseFloat(balance.usd_total_amount) || 0);
        }, 0);

        // If account status is ACTIVE, check balance
        if (status === "ACTIVE") {
          // If balance is insufficient, redirect to setup for step 3 (Add Funds)
          if (totalBalance < AIRWALLEX_CONFIG.MIN_BALANCE_THRESHOLD) {
            navigate("/wallet/setup");
            return;
          }
          // Otherwise, dashboard will be rendered below
        } else if (status) {
          // If account exists but not ACTIVE (CREATED, SUBMITTED, etc.), redirect to setup
          navigate("/wallet/setup");
        } else {
          // If no status field, redirect to setup (no account)
          navigate("/wallet/setup");
        }
      } else {
        // If unexpected response, redirect to setup (safe default)
        navigate("/wallet/setup");
      }
    } catch (error) {
      console.error("Failed to fetch account status:", error);
      // On error, redirect to setup (safe default)
      navigate("/wallet/setup");
    } finally {
      if (showLoadingScreen) {
        setLoading(false);
      }
    }
  }, [navigate]);

  useEffect(() => {
    checkAccountStatus(true); // Show loading screen on initial load
  }, [checkAccountStatus]);

  const handleRefreshData = useCallback(() => {
    // Refresh data WITHOUT showing the full-page loading spinner
    checkAccountStatus(false);
  }, [checkAccountStatus]);

  if (loading) {
    return <LoadingSpinner fullscreen message="Loading account status..." />;
  }

  // If account is ACTIVE, render WalletDashboard
  if (accountStatus === "ACTIVE") {
    return (
      <WalletDashboard
        balances={balances}
        onRefreshData={handleRefreshData}
      />
    );
  }

  // Otherwise, return null (will redirect to setup)
  return null;
};

export default Home;
